import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { paymentModels } from '@nxsys/constants/AppConstants';
import { useGetAWRList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import {
  GetAWRListParams,
  GetNetPayListParams,
} from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { formatFullName, getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import RTWFilterModal, {
  RTWFilterValues,
} from '../../components/RTWFilterModal';
import { getAWRColumns } from '../columns';


const AWRReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { isAgency, isBureau } = useSelector((state: AppState) => state.appStore);

  const dispatch = useDispatch();
  const { awrList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = awrList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IAWR[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingAWRList, run: getAWRList } = useGetAWRList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        {!isAgency && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle prefix={messages['common.awr']} separator="" title="" />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetAWRListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(reportActions.getAWRList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: RTWFilterValues) => {
    const params: GetNetPayListParams = {
      ...queryParams,
      companyIds: values.companies?.map((c) => c.value),
      agencyIds: values.agencies?.map((a) => a.value),
      taxYear: values.taxYear,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IAWR[]) => {
    const dataExport = data.map((item) => {
      const {
        agencyRef,
        systemId,
        id,
        primaryAgency,
        secondaryAgency,
        companyPayment,
        setting,
        awrWeek,
        latestPayroll,
      } = item;
      return {
        id,
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${
          agencyRef || systemId || ''
        }`,
        name: formatFullName(item),
        paymentCompany: companyPayment?.companyName || '',
        paymentModel: setting?.paymentModel
          ? paymentModels[setting?.paymentModel as keyof typeof paymentModels]
          : '',
        awrWeek: awrWeek ?? '',
        taxWeek: latestPayroll?.taxPeriod || '',
        primaryAgencyName: primaryAgency?.name || '',
        secondaryAgency: secondaryAgency?.name || '',
      };
    });

    const headers = [
      'Employee ID',
      'Agency Reference',
      'Name',
      'Payment Company',
      'Payment Model',
      'AWR Week',
      'Tax Week',
      'Primary Agency',
      'Secondary Agency',
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataExport);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.utils.sheet_add_aoa(worksheet, [headers]);
    XLSX.writeFile(
      workbook,
      `AWR-export-on-${moment(new Date()).format('DD/MM/YYYY')}.xlsx`,
      {
        compression: true,
      },
    );
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getAWRList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingAWRList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            {!isAgency && (
              <div className="flex flex-wrap gap-4 items-end">
                <TaxYearSelect
                  initValue={currentTaxYear}
                  onFilter={(value) =>
                    handleFetchData({ ...queryParams, taxYear: value.taxYear })
                  }
                />
              </div>
            )}
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2700, y: 700 }}
            columns={getAWRColumns({
              messages,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <RTWFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default AWRReport;
